import React from 'react';
import PropTypes from 'prop-types';
import RippleButton from './RippleButton';
import { getFullUrl } from 'utils/utils';
import parse from 'html-react-parser';

const PRESERVE_KEYS = [
  'euid_source', 'euid', 'coupon', 'bags',
  'agent_id', 'ta_name', 'ta_email', 'ta_network', 'ta_company',
  'ta_agency', 'iata_number', 'utm_source', 'utm_medium', 'utm_campaign',
  'o_address', 'o_city', 'o_state', 'o_zip', 'o_country', 'o_company', 'o_apt',
  'd_address', 'd_city', 'd_state', 'd_zip', 'd_country', 'd_company', 'd_apt',
  'shipping_from', 'shipping_to', 'o_facility_id', 'd_facility_id',
  'o_cruise_line', 'o_cruise_port', 'o_cruise_itinerary',
  'd_cruise_line', 'd_cruise_port', 'd_cruise_itinerary',
  'products',
];

const buildShipUrl = (baseUrl, { couponCode, subDomain } = {}) => {
  const url = new URL(baseUrl, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);

  // Forward preserved params from the current page URL
  PRESERVE_KEYS.forEach(key => {
    const value = currentParams.get(key);
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  });

  // Add coupon from prop if not already set from URL
  if (couponCode && !url.searchParams.has('coupon')) {
    url.searchParams.set('coupon', couponCode);
  }

  // Add partner subdomain if not already set
  if (subDomain && !url.searchParams.has('partner')) {
    url.searchParams.set('partner', subDomain);
  }

  return url.toString();
};

const SectionShipOverview = ({ items, ctaConfig, shipNowUrl, shipNowButtonText, shipToUrl,
  shipToButtonText, shipFromUrl, shipFromButtonText, name, partnerId,
  couponCode, coupon, subDomain, children, image, imageMobile, imagePosition = 'right' }) => {
  const effectiveCoupon = coupon || couponCode;
  const brandSlug = window.gon.current_brand ? window.gon.current_brand : 'shipsticks';
  const hasImage = image && image.url && image.url.length > 0;
  const isImageLeft = imagePosition === 'left';

  const handleNavigate = (customUrl, defaultPath) => {
    const base = customUrl || getFullUrl(defaultPath, window.gon.subdomain);
    window.location.href = buildShipUrl(base, { couponCode: effectiveCoupon, subDomain });
  };

  return (
    <div className="bg-white text-left px-2 py-10 md:px-24 xl:px-36 2xl:px-60 lg:py-16 w-full">
      <div className={`flex flex-col gap-11 lg:flex-row justify-between
        max-w-7xl mx-auto ${hasImage && !isImageLeft ? 'lg:flex-row-reverse' : ''}`}>

        {hasImage ? (
          <div className="w-full lg:w-1/2 flex items-center justify-center">
            {imageMobile?.url && (
              <img
                src={imageMobile.url}
                alt={'Section image'}
                className="w-full rounded-2xl object-cover block lg:hidden"
              />
            )}
            <img
              src={image.url}
              alt={'Section image'}
              className={`w-full rounded-2xl object-cover ${imageMobile?.url ? 'hidden lg:block' : ''}`}
            />
          </div>
        ) : (
          <div className="w-full px-2 lg:w-2/5">
            {children}
            <br />
            {ctaConfig == 'ship_now' && (
              <RippleButton
                onClick={() => handleNavigate(shipNowUrl, '/ship')}
                className="
                  flex items-center justify-center bg-[#231F20] text-white rounded-full
                  px-5 py-8 my-6 h-[48px] w-full md:w-3/4 lg:w-1/2 lg:max-w-72 text-lg mx-auto lg:mx-0
                "
              >
                {shipNowUrl ? shipNowButtonText : 'Book Now'}
              </RippleButton>
            )}
            {(ctaConfig == 'ship_to' || ctaConfig == 'ship_to_and_from') && (
              <RippleButton
                onClick={() => handleNavigate(shipToUrl, `/ship?shipping_to=${partnerId}`)}
                className="
                  flex items-center justify-center bg-[#231F20] text-white rounded-full
                  px-5 py-8 my-6 h-[48px] w-full text-lg mx-auto lg:mx-0
                "
              >
                {shipToUrl ? shipToButtonText : `Ship To ${name}`}
              </RippleButton>
            )}
            {(ctaConfig == 'ship_from' || ctaConfig == 'ship_to_and_from') && (
              <RippleButton
                onClick={() => handleNavigate(shipFromUrl, `/ship?shipping_from=${partnerId}`)}
                className="
                  flex items-center justify-center bg-[#231F20] text-white rounded-full
                  px-5 py-8 my-6 h-[48px] w-full text-lg mx-auto lg:mx-0
                "
              >
                {shipFromUrl ? shipFromButtonText : `Ship From ${name}`}
              </RippleButton>
            )}
          </div>
        )}

        <div className={`w-full px-2 ${hasImage ? 'lg:w-1/2' : 'lg:w-3/5'}`}>
          {hasImage && children}
          {items && items.length > 0 && items.map((item, index) => (
            <div
              key={index}
              className="
                flex items-start w-full mb-4 bg-white px-8 py-8
                border border-gray-300 rounded-3xl shadow-sm
              ">
              <div className="flex-grow h-auto flex flex-col text-left items-start justify-start">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  <div className={`text-${brandSlug} font-bold inline-block text-3xl pr-4 shrink-0`}>
                    {`0${index+1}`}
                  </div>
                  {parse(item.header)}</h2>
                <div className="text-[18px] leading-[30px]">{parse(item.body)}</div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

SectionShipOverview.propTypes = {
  ctaConfig: PropTypes.string,
  shipNowUrl: PropTypes.string,
  shipNowButtonText: PropTypes.string,
  shipToUrl: PropTypes.string,
  shipToButtonText: PropTypes.string,
  shipFromUrl: PropTypes.string,
  shipFromButtonText: PropTypes.string,
  children: PropTypes.node,
  name: PropTypes.string,
  partnerId: PropTypes.string,
  couponCode: PropTypes.string,
  coupon: PropTypes.string,
  subDomain: PropTypes.string,
  imagePosition: PropTypes.oneOf(['left', 'right']),
  image: PropTypes.shape({
    url: PropTypes.string,
  }),
  imageMobile: PropTypes.shape({
    url: PropTypes.string,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      body: PropTypes.string,
    })
  ),
};


export default SectionShipOverview;
